<script setup lang="ts">
  import { Icon, IconName, IconSize, Input, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, onMounted, watch } from 'vue';
  import type { DepartureCity } from '@/shared/model';
  import { DEPARTURE_CITY_TEXTS } from '../config/texts';
  import { useDepartureCity } from '../model/useDepartureCity';

  interface DepartureCitySearchProps {
    modelValue?: DepartureCity | null;
    cities?: DepartureCity[];
    mobile?: boolean;
  }

  const props = withDefaults(defineProps<DepartureCitySearchProps>(), {
    modelValue: null,
    cities: () => [],
    mobile: false,
  });

  interface DepartureCitySearchEmits {
    (e: 'update:modelValue', city: DepartureCity | null): void;
    (e: 'select', city: DepartureCity): void;
  }

  const emit = defineEmits<DepartureCitySearchEmits>();

  const model = computed<DepartureCity | null>({
    get: () => props.modelValue,
    set: (value: DepartureCity | null) => emit('update:modelValue', value),
  });

  const departure = useDepartureCity({ model, initialCities: props.cities ?? [] });

  watch(
    () => props.cities,
    (list: DepartureCity[]) => departure.setCities(list ?? []),
  );

  const isEmptyResult = computed(
    () => departure.inputString.value.trim() !== '' && departure.optionsList.value.length === 0,
  );

  const onSelect = (city: DepartureCity): void => {
    departure.selectCity(city);
    emit('select', city);
  };

  onMounted(() => {
    departure.open();
  });

  defineExpose({
    validate: (): boolean => departure.validate(),
  });
</script>

<template>
  <div
    class="departure-city-search"
    :class="{ 'departure-city-search_mobile': mobile }"
    data-testid="departure-city-search"
  >
    <div class="departure-city-search__input">
      <Input
        :model-value="departure.inputString.value"
        :placeholder="DEPARTURE_CITY_TEXTS.searchPlaceholder"
        :bg="false"
        bordered
        size="l"
        name="departure-city-search"
        data-testid="departure-city-search-input"
        @update:modelValue="departure.setInput"
      >
        <template #append>
          <button
            v-if="departure.clearVisible.value"
            type="button"
            class="departure-city-search__icon-btn"
            data-testid="departure-city-search-clear"
            aria-label="Очистить"
            @click.stop.prevent="departure.clear"
          >
            <Icon
              :name="IconName.Close"
              :size="IconSize.S"
            />
          </button>
          <Icon
            v-else
            :name="IconName.Search"
            :size="IconSize.S"
            class="departure-city-search__search-icon"
          />
        </template>
      </Input>
    </div>

    <Typo
      v-if="isEmptyResult"
      class="departure-city-search__empty"
      data-testid="departure-city-search-empty"
      :variant="TypoVariant.BodyLRegular"
      tag="p"
    >
      {{ DEPARTURE_CITY_TEXTS.nothingFoundHint }}
    </Typo>

    <ul
      v-else
      class="departure-city-search__list"
      data-testid="departure-city-search-list"
    >
      <li
        v-for="city in departure.optionsList.value"
        :key="city.id"
      >
        <button
          type="button"
          class="departure-city-search__option"
          data-testid="departure-city-search-option"
          @click="onSelect(city)"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ city.name }}
          </Typo>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
  .departure-city-search {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-m);
    width: 100%;

    ::v-deep input[name='departure-city-search'] {
      font-size: 16px;
    }

    &_mobile {
      gap: 0;

      .departure-city-search__input {
        background: var(--color-bg-primary);
        padding-bottom: var(--spacing-xs);
        position: sticky;
        top: 0;
        z-index: 2;

        &::after {
          background: var(--color-bg-primary);
          content: '';
          display: block;
          height: 10px;
          position: absolute;
          top: -10px;
          width: 100%;
        }
      }
    }

    &__icon-btn {
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      display: inline-flex;
      fill: var(--color-text-secondary);
      justify-content: center;
      padding: 0;
    }

    &__search-icon {
      fill: var(--color-text-secondary);
    }

    &__empty {
      fill: var(--color-text-primary);
      margin: 0;
      white-space: pre-line;
    }

    &__list {
      display: flex;
      flex-direction: column;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    &__option {
      align-items: center;
      background: none;
      border: none;
      box-sizing: border-box;
      color: var(--color-text-primary);
      cursor: pointer;
      display: flex;
      min-height: 48px;
      padding: var(--spacing-xs) 0;
      text-align: left;
      width: 100%;
    }
  }
</style>
